import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Box } from '@mui/system';
import { Dialog, DialogContent, Button, DialogActions } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { infoModelStyles } from './styles';
import { customTinyMce } from '../Procedure/styles';
import { LrsVerbs } from 'GeminiViewerComponent/features/xAPI/xApiData';
import { saveStatement } from 'GeminiViewerComponent/features/xAPI/xapiSlice';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { getActiveNewsFlash } from 'GeminiViewerComponent/features/newsFlash/newsFlashSlice';
import useContentLinkInterceptor from 'GeminiViewerComponent/hooks/useContentLinkInterceptor';
import { contentService } from 'features/services';
import clsx from 'clsx';

const NewsFlashDialog = ({ openDialog, setOpenDialog }) => {
    const dispatch = useDispatch();
    const theme = useSelector(selectActiveTheme);
    const classes = infoModelStyles(theme);
    const tinyMceCustomClass = customTinyMce(theme);
    const newsFlash = useSelector(getActiveNewsFlash);
    const [remainingDisplayTime, setRemainingDisplayTime] = useState(0);
    const [closeEnabled, setCloseEnabled] = useState(false);

    useContentLinkInterceptor('.newsflash-content', contentService);

    const dialogStyle = useMemo(() => {
        const margin = '10px';
        const style = {
            borderRadius: '10px',
            maxWidth: 'unset',
            width: '100vw',
        };
        if (newsFlash.window_width) {
            style.width = newsFlash.window_width + '%';
        }
        if (newsFlash.window_height) {
            style.height = newsFlash.window_height + '%';
        }
        switch (newsFlash.window_position) {
            case 'CenterLeft':
                style.margin = `auto auto auto ${margin}`;
                break;
            case 'CenterRight':
                style.margin = `auto ${margin} auto auto`;
                break;
            case 'TopLeft':
                style.margin = `${margin} auto auto ${margin}`;
                break;
            case 'TopCenter':
                style.margin = `${margin} auto auto`;
                break;
            case 'TopRight':
                style.margin = `${margin} ${margin} auto auto`;
                break;
            case 'BottomLeft':
                style.margin = `auto auto ${margin} ${margin}`;
                break;
            case 'BottomCenter':
                style.margin = `auto auto ${margin}`;
                break;
            case 'BottomRight':
                style.margin = `auto ${margin} ${margin} auto`;
                break;
            case 'CenterCenter':
            default:
                style.margin = `${margin}`;
                break;
        }
        return style;
    }, [newsFlash]);

    const handleClose = () => {
        if (newsFlash !== null && closeEnabled) {
            setOpenDialog(newsFlash);
        }
    };

    useEffect(() => {
        if (!openDialog) {
            return;
        }

        if (newsFlash !== null) {
            setCloseEnabled(false);
            if (newsFlash.minimum_display_time === null || newsFlash.minimum_display_time === undefined) {
                setCloseEnabled(true);
            } else {
                setCloseEnabled(false);
                setRemainingDisplayTime(newsFlash.minimum_display_time);
                (function loop(n) {
                    setTimeout(() => {
                        setRemainingDisplayTime(n);

                        if (n > 0) {
                            loop(n - 1);
                        } else {
                            setCloseEnabled(true);
                        }
                    }, 1000);
                })(newsFlash.minimum_display_time - 1);
            }

            dispatch(
                saveStatement({
                    LrsVerb: LrsVerbs.Viewed,
                    response: {
                        action: `News Flash ${newsFlash.display_name} (${newsFlash.news_flash_id})`,
                    },
                })
            );
        }
    }, [newsFlash, openDialog]);

    const contentRef = useRef(null);

    useEffect(() => {
        const hasAutoplay = newsFlash.content?.includes('autoplay');

        if (!hasAutoplay) return;

        const setupVideos = () => {
            const videos = contentRef.current?.getElementsByTagName('video') || [];

            console.log('Found videos:', videos.length);

            Array.from(videos).forEach((video) => {
                video.muted = true;
                video.defaultMuted = true;
                video.setAttribute('muted', '');
                video.setAttribute('playsinline', '');
                video.setAttribute('autoplay', '');
                video.autoplay = true;

                video.play().catch((error) => {
                    console.log('Autoplay blocked:', error);
                });
            });
        };

        const unmuteAndPlayVideos = () => {
            console.log('User interaction detected');

            const videos = contentRef.current?.getElementsByTagName('video') || [];
            console.log('Videos to unmute:', videos.length);

            Array.from(videos).forEach((video) => {
                console.log('unmuted');
                video.muted = false;
                video.defaultMuted = false;
                video.removeAttribute('muted');

                video.play().catch((error) => {
                    console.log('Play with sound blocked:', error);
                });
            });

            document.removeEventListener('click', unmuteAndPlayVideos, true);
            document.removeEventListener('touchstart', unmuteAndPlayVideos, true);
            document.removeEventListener('pointerdown', unmuteAndPlayVideos, true);
            document.removeEventListener('loadedmetadata', unmuteAndPlayVideos, true);
            document.removeEventListener('DOMContentLoaded', unmuteAndPlayVideos, true);
        };

        const timer = setTimeout(setupVideos, 0);

        document.addEventListener('click', unmuteAndPlayVideos, true);
        document.addEventListener('touchstart', unmuteAndPlayVideos, true);
        document.addEventListener('pointerdown', unmuteAndPlayVideos, true);
        document.addEventListener('loadedmetadata', unmuteAndPlayVideos, true);
        document.addEventListener('DOMContentLoaded', unmuteAndPlayVideos, true);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', unmuteAndPlayVideos, true);
            document.removeEventListener('touchstart', unmuteAndPlayVideos, true);
            document.removeEventListener('pointerdown', unmuteAndPlayVideos, true);
            document.removeEventListener('loadedmetadata', unmuteAndPlayVideos, true);
            document.removeEventListener('DOMContentLoaded', unmuteAndPlayVideos, true);
        };
    }, [newsFlash.content]);

    return (
        <Dialog
            open={openDialog}
            onClose={handleClose}
            fullWidth={!newsFlash.window_width}
            PaperProps={{
                style: dialogStyle,
            }}
        >
            <DialogContent className={classes.dialogContainer}>
                <Box className={classes.dialogWrapper}>
                    <Box
                        ref={contentRef}
                        className={clsx(
                            classes.newsFlashContent,
                            `newsflash-content ${tinyMceCustomClass.tinymceCustom}`
                        )}
                        m={0}
                        dangerouslySetInnerHTML={{
                            __html: newsFlash.content,
                        }}
                    ></Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    className={classes.close}
                    onClick={handleClose}
                    key="newsflash-close-btn"
                    variant="contained"
                    color="primary"
                    disableElevation
                    fullWidth={true}
                    disabled={!closeEnabled}
                >
                    Close
                    {remainingDisplayTime > 0 ? ` (${remainingDisplayTime})` : ''}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export { NewsFlashDialog };
